import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Package, Plus, Pencil, Trash2, ChevronDown, ChevronRight, 
  GripVertical, Layers, Tags, Settings2, XCircle
} from 'lucide-react';

// Drag and drop imports
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// Helper to parse API error messages
const getErrorMessage = (error, fallback = 'An error occurred') => {
  const detail = error.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e.msg || String(e)).join(', ');
  if (detail?.msg) return detail.msg;
  return fallback;
};

// Helper to auto-generate attribute_key from name
const generateAttributeKey = (name) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, '')
    .replace(/\s+/g, '_')
    .substring(0, 50);
};

// Sortable Attribute Item Component
function SortableAttributeItem({ 
  attrKey, 
  idx, 
  attr, 
  values, 
  isExpanded, 
  productId,
  onToggleExpand,
  onEdit,
  onRemove,
  onAddValue,
  onEditValue,
  onDeleteValue
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: attrKey });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg">
      <div className="flex items-center gap-2 p-3 bg-muted/30">
        <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
          <GripVertical className="w-4 h-4 text-muted-foreground" />
        </div>
        <Badge variant="secondary" className="text-xs">{idx + 1}</Badge>
        <span className="font-medium flex-1">{attr?.attribute_name || attrKey}</span>
        
        {attr?.code_prefix && (
          <Badge variant="outline" className="text-xs font-mono">{attr.code_prefix}</Badge>
        )}
        {attr?.include_in_code === false && (
          <Badge variant="outline" className="text-xs text-orange-600">Not in Code</Badge>
        )}
        {attr?.allow_multiple && <Badge variant="outline" className="text-xs">Multi</Badge>}
        <Badge variant="outline" className="text-xs">{values.filter(v => v.is_active).length} values</Badge>
        
        <div className="flex gap-1">
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onEdit(attr)}>
            <Pencil className="w-3 h-3" />
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => onToggleExpand(productId, attrKey)}>
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => onRemove(productId, attrKey)}>
            <XCircle className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {isExpanded && (
        <div className="p-3 border-t bg-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium flex items-center gap-1">
              <Tags className="w-3 h-3" /> Values
            </span>
            <Button variant="outline" size="sm" className="h-7" onClick={() => onAddValue(attrKey)}>
              <Plus className="w-3 h-3 mr-1" /> Add Value
            </Button>
          </div>
          {values.length === 0 ? (
            <p className="text-sm text-muted-foreground">No values defined</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {[...values].sort((a, b) => a.value_name.localeCompare(b.value_name)).map(val => (
                <div key={val.id} className={`flex items-center gap-1 px-2 py-1 rounded border ${!val.is_active ? 'opacity-50' : ''}`}>
                  <span className="text-sm">{val.value_name}</span>
                  <code className="text-xs bg-muted px-1 rounded">{val.value_code}</code>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0" onClick={() => onEditValue(attrKey, val)}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-500" onClick={() => onDeleteValue(attrKey, val.id)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function ItemCodeConstructorPage() {
  const [products, setProducts] = useState([]);
  const [allAttributes, setAllAttributes] = useState([]);
  const [allValues, setAllValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedProduct, setExpandedProduct] = useState(null); // Single expanded product (accordion style)
  const [expandedAttribute, setExpandedAttribute] = useState(null); // Single expanded attribute within product

  // Product dialog
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '', code_prefix: '', description: '', attribute_sequence: [], is_active: true
  });

  // Attribute dialog
  const [attributeDialogOpen, setAttributeDialogOpen] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);
  const [attributeForm, setAttributeForm] = useState({
    attribute_name: '', 
    allow_multiple: false, 
    max_selections: null, 
    repeat_prefix: false, 
    code_prefix: '',
    include_in_code: true,
    description: '', 
    is_active: true
  });
  const [targetProductId, setTargetProductId] = useState(null);

  // Value dialog
  const [valueDialogOpen, setValueDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [valueForm, setValueForm] = useState({
    value_name: '', value_code: '', sort_order: 0, is_active: true
  });
  const [targetAttributeKey, setTargetAttributeKey] = useState(null);

  const [saving, setSaving] = useState(false);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel - single call for all attribute values
      const [productsRes, attributesRes, valuesRes] = await Promise.all([
        axios.get(`${API_URL}/api/item-code-generator/products`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/item-code-generator/attributes`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/item-code-generator/attributes-values/all`, { headers: getAuthHeader() })
      ]);
      
      setProducts(productsRes.data || []);
      setAllAttributes(attributesRes.data || []);
      setAllValues(valuesRes.data || {});
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Toggle product expansion
  const toggleProduct = (productId) => {
    setExpandedProduct(prev => prev === productId ? null : productId);
    setExpandedAttribute(null); // Collapse any expanded attribute when switching products
  };

  // Toggle attribute expansion within a product
  const toggleAttribute = (productId, attrKey) => {
    const key = `${productId}-${attrKey}`;
    setExpandedAttribute(prev => prev === key ? null : key);
  };

  // Get attribute by key
  const getAttributeByKey = (key) => allAttributes.find(a => a.attribute_key === key);

  // Handle drag end for attribute reordering
  const handleDragEnd = async (event, productId) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) return;
    
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    const oldIndex = product.attribute_sequence.indexOf(active.id);
    const newIndex = product.attribute_sequence.indexOf(over.id);
    
    if (oldIndex === -1 || newIndex === -1) return;
    
    const newSequence = arrayMove(product.attribute_sequence, oldIndex, newIndex);
    
    // Optimistically update UI
    setProducts(prev => prev.map(p => 
      p.id === productId ? { ...p, attribute_sequence: newSequence } : p
    ));
    
    try {
      await axios.patch(`${API_URL}/api/item-code-generator/products/${productId}`, {
        attribute_sequence: newSequence
      }, { headers: getAuthHeader() });
    } catch (error) {
      toast.error(getErrorMessage(error, 'Cannot reorder - codes exist'));
      fetchData(); // Revert on error
    }
  };

  // ==================== PRODUCT HANDLERS ====================
  const openProductDialog = (product = null) => {
    setEditingProduct(product);
    setProductForm(product ? {
      name: product.name,
      code_prefix: product.code_prefix,
      description: product.description || '',
      attribute_sequence: product.attribute_sequence || [],
      is_active: product.is_active
    } : {
      name: '', code_prefix: '', description: '', attribute_sequence: [], is_active: true
    });
    setProductDialogOpen(true);
  };

  const saveProduct = async () => {
    if (!productForm.name.trim()) return toast.error('Product name is required');
    if (!productForm.code_prefix.trim()) return toast.error('Code prefix is required');
    if (!/^[A-Z0-9 ]+$/.test(productForm.code_prefix)) return toast.error('Prefix must be uppercase letters, numbers, and spaces only');

    setSaving(true);
    try {
      if (editingProduct) {
        await axios.patch(`${API_URL}/api/item-code-generator/products/${editingProduct.id}`, productForm, { headers: getAuthHeader() });
        toast.success('Product updated');
      } else {
        await axios.post(`${API_URL}/api/item-code-generator/products`, productForm, { headers: getAuthHeader() });
        toast.success('Product created');
      }
      setProductDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save product'));
    } finally {
      setSaving(false);
    }
  };

  // ==================== ATTRIBUTE HANDLERS ====================
  const openAttributeDialog = (productId, attribute = null) => {
    setTargetProductId(productId);
    setEditingAttribute(attribute);
    setAttributeForm(attribute ? {
      attribute_name: attribute.attribute_name,
      allow_multiple: attribute.allow_multiple,
      max_selections: attribute.max_selections,
      repeat_prefix: attribute.repeat_prefix,
      code_prefix: attribute.code_prefix || '',
      include_in_code: attribute.include_in_code !== false, // Default true
      description: attribute.description || '',
      is_active: attribute.is_active
    } : {
      attribute_name: '', 
      allow_multiple: false, 
      max_selections: null, 
      repeat_prefix: false, 
      code_prefix: '',
      include_in_code: true,
      description: '', 
      is_active: true
    });
    setAttributeDialogOpen(true);
  };

  const saveAttribute = async () => {
    if (!attributeForm.attribute_name.trim()) return toast.error('Attribute name is required');

    setSaving(true);
    try {
      let attrKey = editingAttribute?.attribute_key;
      
      const payload = {
        attribute_name: attributeForm.attribute_name,
        allow_multiple: attributeForm.allow_multiple,
        max_selections: attributeForm.allow_multiple ? attributeForm.max_selections : null,
        repeat_prefix: attributeForm.allow_multiple ? attributeForm.repeat_prefix : false,
        code_prefix: attributeForm.code_prefix?.toUpperCase() || null,
        include_in_code: attributeForm.include_in_code,
        description: attributeForm.description,
        is_active: attributeForm.is_active
      };
      
      if (editingAttribute) {
        // Update existing attribute
        await axios.patch(
          `${API_URL}/api/item-code-generator/attributes/${attrKey}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Attribute updated');
      } else {
        // Create new attribute with auto-generated key
        attrKey = generateAttributeKey(attributeForm.attribute_name);
        await axios.post(`${API_URL}/api/item-code-generator/attributes`, {
          ...payload,
          attribute_key: attrKey
        }, { headers: getAuthHeader() });
        toast.success('Attribute created');

        // Add to product's attribute_sequence if targetProductId exists
        if (targetProductId) {
          const product = products.find(p => p.id === targetProductId);
          if (product) {
            const newSequence = [...(product.attribute_sequence || []), attrKey];
            await axios.patch(`${API_URL}/api/item-code-generator/products/${targetProductId}`, {
              attribute_sequence: newSequence
            }, { headers: getAuthHeader() });
          }
        }
      }
      setAttributeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save attribute'));
    } finally {
      setSaving(false);
    }
  };

  const removeAttributeFromProduct = async (productId, attrKey) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;

    const newSequence = (product.attribute_sequence || []).filter(k => k !== attrKey);
    try {
      await axios.patch(`${API_URL}/api/item-code-generator/products/${productId}`, {
        attribute_sequence: newSequence
      }, { headers: getAuthHeader() });
      toast.success('Attribute removed from product');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to remove attribute'));
    }
  };

  const addExistingAttributeToProduct = async (productId, attrKey) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    if ((product.attribute_sequence || []).includes(attrKey)) {
      return toast.error('Attribute already added to this product');
    }

    const newSequence = [...(product.attribute_sequence || []), attrKey];
    try {
      await axios.patch(`${API_URL}/api/item-code-generator/products/${productId}`, {
        attribute_sequence: newSequence
      }, { headers: getAuthHeader() });
      toast.success('Attribute added to product');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add attribute'));
    }
  };

  // ==================== VALUE HANDLERS ====================
  const openValueDialog = (attrKey, value = null) => {
    setTargetAttributeKey(attrKey);
    setEditingValue(value);
    setValueForm(value ? {
      value_name: value.value_name,
      value_code: value.value_code,
      sort_order: value.sort_order || 0,
      is_active: value.is_active
    } : {
      value_name: '', value_code: '', sort_order: 0, is_active: true
    });
    setValueDialogOpen(true);
  };

  const saveValue = async () => {
    if (!valueForm.value_name.trim()) return toast.error('Value name is required');
    if (!valueForm.value_code.trim()) return toast.error('Value code is required');
    if (valueForm.value_code.includes('-') || valueForm.value_code.includes('+')) {
      return toast.error('Value code cannot contain - or + (reserved separators)');
    }

    setSaving(true);
    try {
      const payload = {
        value_name: valueForm.value_name,
        value_code: valueForm.value_code.toUpperCase(),
        sort_order: valueForm.sort_order,
        is_active: valueForm.is_active,
        attribute_key: targetAttributeKey
      };

      if (editingValue) {
        await axios.patch(
          `${API_URL}/api/item-code-generator/attributes/${targetAttributeKey}/values/${editingValue.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Value updated');
      } else {
        await axios.post(
          `${API_URL}/api/item-code-generator/attributes/${targetAttributeKey}/values`,
          payload,
          { headers: getAuthHeader() }
        );
        toast.success('Value created');
      }
      setValueDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save value'));
    } finally {
      setSaving(false);
    }
  };

  const deleteValue = async (attrKey, valueId) => {
    if (!window.confirm('Delete this value?')) return;
    try {
      await axios.delete(
        `${API_URL}/api/item-code-generator/attributes/${attrKey}/values/${valueId}`,
        { headers: getAuthHeader() }
      );
      toast.success('Value deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete value'));
    }
  };

  // Get unused attributes for a product (for "Add Existing" dropdown)
  const getUnusedAttributes = (product) => {
    const used = product.attribute_sequence || [];
    return allAttributes.filter(a => !used.includes(a.attribute_key) && a.is_active);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="item-code-constructor-page">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings2 className="w-6 h-6" />
              Item Code Constructor
            </h1>
            <p className="text-muted-foreground">Configure products and their attributes for automatic code generation</p>
          </div>
          <Button onClick={() => openProductDialog()} data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>

        {/* Products List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading...</div>
        ) : products.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              No products configured. Create your first product to get started.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {products.map((product) => (
              <Card key={product.id} className={!product.is_active ? 'opacity-60' : ''}>
                <Collapsible open={expandedProduct === product.id} onOpenChange={() => toggleProduct(product.id)}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {expandedProduct === product.id ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                          <Package className="w-5 h-5 text-primary" />
                          <div>
                            <CardTitle className="text-lg">{product.name}</CardTitle>
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="font-mono">{product.code_prefix}</Badge>
                              <span className="text-sm text-muted-foreground">
                                {(product.attribute_sequence || []).length} attributes
                              </span>
                              {product.code_count > 0 && (
                                <Badge variant="secondary">{product.code_count} codes generated</Badge>
                              )}
                              {!product.is_active && <Badge variant="outline" className="text-orange-600">Inactive</Badge>}
                            </div>
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openProductDialog(product); }}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <CardContent className="pt-0 border-t">
                      <div className="space-y-3 mt-4">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium flex items-center gap-2">
                            <Layers className="w-4 h-4" />
                            Attributes (drag to reorder)
                          </h4>
                          <div className="flex gap-2">
                            {getUnusedAttributes(product).length > 0 && (
                              <select 
                                className="text-sm border rounded px-2 py-1"
                                onChange={(e) => { if (e.target.value) addExistingAttributeToProduct(product.id, e.target.value); e.target.value = ''; }}
                                defaultValue=""
                              >
                                <option value="">Add existing attribute...</option>
                                {getUnusedAttributes(product).map(attr => (
                                  <option key={attr.attribute_key} value={attr.attribute_key}>{attr.attribute_name}</option>
                                ))}
                              </select>
                            )}
                            <Button variant="outline" size="sm" onClick={() => openAttributeDialog(product.id)}>
                              <Plus className="w-3 h-3 mr-1" />
                              New Attribute
                            </Button>
                          </div>
                        </div>

                        {(product.attribute_sequence || []).length === 0 ? (
                          <p className="text-sm text-muted-foreground py-4 text-center">No attributes added yet</p>
                        ) : (
                          <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={(event) => handleDragEnd(event, product.id)}
                          >
                            <SortableContext
                              items={product.attribute_sequence || []}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="space-y-2">
                                {(product.attribute_sequence || []).map((attrKey, idx) => {
                                  const attr = getAttributeByKey(attrKey);
                                  const values = allValues[attrKey] || [];
                                  const isExpanded = expandedAttribute === `${product.id}-${attrKey}`;

                                  return (
                                    <SortableAttributeItem
                                      key={attrKey}
                                      attrKey={attrKey}
                                      idx={idx}
                                      attr={attr}
                                      values={values}
                                      isExpanded={isExpanded}
                                      productId={product.id}
                                      onToggleExpand={toggleAttribute}
                                      onEdit={(a) => openAttributeDialog(product.id, a)}
                                      onRemove={removeAttributeFromProduct}
                                      onAddValue={openValueDialog}
                                      onEditValue={openValueDialog}
                                      onDeleteValue={deleteValue}
                                    />
                                  );
                                })}
                              </div>
                            </SortableContext>
                          </DndContext>
                        )}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </div>
        )}

        {/* Product Dialog */}
        <Dialog open={productDialogOpen} onOpenChange={setProductDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingProduct ? 'Edit Product' : 'Add Product'}</DialogTitle>
              <DialogDescription>Configure the product for item code generation</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Product Name *</Label>
                  <Input value={productForm.name} onChange={(e) => setProductForm({ ...productForm, name: e.target.value })} placeholder="e.g., Banquet Chair" data-testid="product-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Code Prefix *</Label>
                  <Input value={productForm.code_prefix} onChange={(e) => setProductForm({ ...productForm, code_prefix: e.target.value.toUpperCase() })} placeholder="e.g., CHAIR" maxLength={50} data-testid="product-prefix-input" />
                  <p className="text-xs text-muted-foreground">Uppercase letters, numbers, and spaces allowed</p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea value={productForm.description} onChange={(e) => setProductForm({ ...productForm, description: e.target.value })} rows={2} />
              </div>
              <div className="flex items-center gap-2">
                <Switch checked={productForm.is_active} onCheckedChange={(checked) => setProductForm({ ...productForm, is_active: checked })} />
                <Label>Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setProductDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveProduct} disabled={saving} data-testid="save-product-btn">{saving ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Attribute Dialog - Now with prefix options */}
        <Dialog open={attributeDialogOpen} onOpenChange={setAttributeDialogOpen}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingAttribute ? 'Edit Attribute' : 'Add Attribute'}</DialogTitle>
              <DialogDescription>Define an attribute type for code generation</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Attribute Name *</Label>
                <Input 
                  value={attributeForm.attribute_name} 
                  onChange={(e) => setAttributeForm({ ...attributeForm, attribute_name: e.target.value })} 
                  placeholder="e.g., Frame Material" 
                  data-testid="attribute-name-input" 
                />
              </div>
              
              {/* Code Generation Options */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                <h4 className="font-medium text-sm">Code Generation Options</h4>
                
                <div className="flex items-center justify-between">
                  <div>
                    <Label>Include in Code</Label>
                    <p className="text-xs text-muted-foreground">Whether this attribute appears in the generated item code</p>
                  </div>
                  <Switch 
                    checked={attributeForm.include_in_code} 
                    onCheckedChange={(checked) => setAttributeForm({ ...attributeForm, include_in_code: checked })} 
                  />
                </div>
                
                {attributeForm.include_in_code && (
                  <div className="space-y-2">
                    <Label>Code Prefix (optional)</Label>
                    <Input 
                      value={attributeForm.code_prefix} 
                      onChange={(e) => setAttributeForm({ ...attributeForm, code_prefix: e.target.value.toUpperCase() })} 
                      placeholder="e.g., FRM, LAM, PWD" 
                      maxLength={10}
                    />
                    <p className="text-xs text-muted-foreground">Prefix appears before value in code (e.g., "PWD BLACK" instead of just "BLACK")</p>
                  </div>
                )}
              </div>

              {/* Multiple Selection Options */}
              <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                <div className="flex items-center gap-2">
                  <Switch 
                    checked={attributeForm.allow_multiple} 
                    onCheckedChange={(checked) => setAttributeForm({ ...attributeForm, allow_multiple: checked })} 
                  />
                  <Label>Allow Multiple Selections</Label>
                </div>
                {attributeForm.allow_multiple && (
                  <>
                    <div className="space-y-2">
                      <Label>Max Selections (optional)</Label>
                      <Input 
                        type="number" 
                        min="2" 
                        value={attributeForm.max_selections || ''} 
                        onChange={(e) => setAttributeForm({ ...attributeForm, max_selections: e.target.value ? parseInt(e.target.value) : null })} 
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch 
                        checked={attributeForm.repeat_prefix} 
                        onCheckedChange={(checked) => setAttributeForm({ ...attributeForm, repeat_prefix: checked })} 
                      />
                      <Label>Repeat Prefix for Each Value</Label>
                    </div>
                  </>
                )}
              </div>

              {/* Preview */}
              {attributeForm.attribute_name && (
                <div className="p-3 bg-primary/10 rounded-lg">
                  <span className="text-xs text-muted-foreground uppercase">Code Preview Example:</span>
                  <div className="mt-1">
                    <code className="text-sm font-mono font-semibold text-primary">
                      ...{attributeForm.include_in_code 
                        ? (attributeForm.code_prefix ? `${attributeForm.code_prefix} VALUE` : 'VALUE')
                        : '(not included)'}...
                    </code>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Switch 
                  checked={attributeForm.is_active} 
                  onCheckedChange={(checked) => setAttributeForm({ ...attributeForm, is_active: checked })} 
                />
                <Label>Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAttributeDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveAttribute} disabled={saving} data-testid="save-attribute-btn">{saving ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Value Dialog - Simplified (prefix moved to attribute level) */}
        <Dialog open={valueDialogOpen} onOpenChange={setValueDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingValue ? 'Edit Value' : 'Add Value'}</DialogTitle>
              <DialogDescription>Define a value option for the attribute</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Value Name *</Label>
                <Input 
                  value={valueForm.value_name} 
                  onChange={(e) => setValueForm({ ...valueForm, value_name: e.target.value })} 
                  placeholder="e.g., Powder Coating Black" 
                  data-testid="value-name-input" 
                />
              </div>
              <div className="space-y-2">
                <Label>Value Code *</Label>
                <Input 
                  value={valueForm.value_code} 
                  onChange={(e) => setValueForm({ ...valueForm, value_code: e.target.value.toUpperCase() })} 
                  placeholder="e.g., BLACK, 1.8MM" 
                  data-testid="value-code-input" 
                />
                <p className="text-xs text-muted-foreground">Letters, numbers, spaces, periods (.) allowed. Cannot contain - or +</p>
              </div>
              
              {valueForm.value_code && (
                <div className="p-3 bg-primary/10 rounded-lg">
                  <span className="text-xs text-muted-foreground uppercase">Code Preview:</span>
                  <div className="mt-1">
                    <code className="text-lg font-mono font-semibold text-primary">
                      ...{valueForm.value_code}...
                    </code>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Note: If the attribute has a prefix configured, it will appear before this value.
                  </p>
                </div>
              )}
              
              <div className="flex items-center gap-2">
                <Switch 
                  checked={valueForm.is_active} 
                  onCheckedChange={(checked) => setValueForm({ ...valueForm, is_active: checked })} 
                />
                <Label>Active</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setValueDialogOpen(false)}>Cancel</Button>
              <Button onClick={saveValue} disabled={saving} data-testid="save-value-btn">{saving ? 'Saving...' : 'Save'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
